<template>
  <div class="rows mobile-cols">
    <div class="half">
      <DataGrid
          :actions-header="$t('settings.on-board.groups')"
          :data-model="dataGridModel"
          :data="filteredGroups"
          :selected-id="selectedGroupId"
          @on-select="dataGridSelectGroupId"
          @on-delete="deleteGroupId"
          allow-add
          allow-edit
          allow-delete
      />
    </div>
    <div class="half">
      <div class="data-grid-form cols">
        <div class="cols">
          <div>
            <label for="groupName">
              {{ $t('settings.on-board.users-name') }}
            </label>
            <input
                id="groupName"
                class="stretch"
                v-model="name"
            />
          </div>
          <div>
            <button type="submit" class="button-save" @click="save">
              {{ $t('generic.save') }}
            </button>
          </div>
        </div>

        <h3>{{ $t('settings.on-board.tasks') }}</h3>
        <TaskList :tasks="tasks"/>
      </div>
    </div>
  </div>
</template>

<script>
import DataGrid from '@/components/pages/partials/DataGrid';
import {actions} from '@/assets/js/vue/vuex/actions';
import notification from '@/assets/js/notification';
import {mapState} from 'vuex';
import {dataAggregator, entityDataManager} from '@/assets/js/data-manipulation';
import TaskList from '@/components/pages/partials/TaskList';

export default {
  name: 'Groups',
  components: {DataGrid, TaskList},
  props: {
    searchToken: String,
    selectedUserId: [Number, null],
    selectedGroupId: [Number, null],
  },
  data() {
    return {
      name: null,

      tasks: {
        taskCreateGroup: {
          text: this.$t('settings.on-board.users.tasks.create-group'),
          status: 'pending',
        },
        taskCreateGroupLink: {
          text: this.$t('settings.on-board.users.tasks.create-group-link'),
          status: 'pending',
        },
      },
    };
  },
  computed: {
    ...mapState(['groups', 'devices', 'vehicles']),
    dataGridModel() {
      return {
        headers: {
          [this.$t('settings.on-board.groups-name')]: 'name',
        },
      };
    },
    filteredGroups() {
      return dataAggregator.filterListByToken(this.groups, ['name'], this.searchToken);
    },
  },
  watch: {
    selectedGroupId(id) {
      this.fillFormById(id);
    },
  },
  methods: {
    dataGridSelectGroupId(id) {
      this.selectGroupId(id);
      this.resetTasks();
    },
    selectGroupId(id) {
      this.$emit('on-select-group', id);
    },
    resetTasks() {
      for (let task in this.tasks) {
        this.tasks[task].status = 'pending';
      }
    },
    succeedTask(task) {
      this.tasks[task].status = 'success';
    },
    failTask(task) {
      this.tasks[task].status = 'fail';
    },
    fillFormById(id) {
      if (id === null) {
        this.name = null;

        return;
      }

      const group = entityDataManager.getById(this.groups, id);

      this.name = group.name;
    },
    async save() {
      if (this.selectedUserId === null) {
        notification.error(this.$t('settings.on-board.groups.user-required'));
        return;
      }

      const isNewGroup = this.selectedGroupId === null;
      const groupPayload = {
        id: this.selectedGroupId,
        name: this.name,
      };

      const group = await this.saveGroup(groupPayload);

      if (group === null) {
        return;
      }

      if (isNewGroup) {
        const groupPermissionPayload = {
          userId: this.selectedUserId,
          groupId: group.id,
        };

        await this.saveGroupPermission(groupPermissionPayload);

        const attributes = await this.$store.dispatch(actions.GET_ATTRIBUTES);
        const motionTimeAttribute = attributes.find(attribute => attribute.attribute === 'motionTime');

        if (motionTimeAttribute) {
          const groupAttributePermissions = {
            groupId: group.id,
            attributeId: motionTimeAttribute.id,
          };
          await this.saveGroupPermission(groupAttributePermissions);
        }

        const fuelLevelPercentageAttribute = attributes.find(attribute => attribute.attribute === 'fuelLevelPercentage');

        if (fuelLevelPercentageAttribute) {
          const groupAttributePermissions = {
            groupId: group.id,
            attributeId: fuelLevelPercentageAttribute.id,
          };
          await this.saveGroupPermission(groupAttributePermissions);
        }

      } else {
        this.succeedTask('taskCreateGroupLink');
      }

      await this.$store.dispatch(actions.GET_GROUPS, {userId: this.selectedUserId}).
          catch(e => notification.error(this.$t(e.message)));

      this.selectGroupId(group.id);
    },
    async saveGroup(payload) {
      let resultGroup = null;

      const action = payload.id !== null ? actions.EDIT_GROUP : actions.CREATE_GROUP;

      await this.$store.dispatch(action, payload).
          then(
              group => {
                resultGroup = group;
                this.succeedTask('taskCreateGroup');
              },
          ).
          catch(() => this.failTask('taskCreateGroup'));

      return resultGroup;
    },
    async saveGroupPermission(payload) {
      await this.$store.dispatch(actions.CREATE_PERMISSION, payload).
          then(() => this.succeedTask('taskCreateGroupLink')).
          catch(() => this.failTask('taskCreateGroupLink'));
    },
    deleteGroupId(id) {
      if (this.selectedUserId === null) {
        notification.error(this.$t('settings.on-board.groups.user-required'));
        return;
      }

      if (confirm(this.$t('generic.delete.confirm'))) {
        this.deleteGroupDevices(id);

        this.$store.dispatch(actions.DELETE_GROUP, {id}).
            then(() => {
              this.selectGroupId(null);
              this.$store.dispatch(actions.GET_GROUPS, {userId: this.selectedUserId}).
                  catch(e => notification.error(this.$t(e.message)));

              notification.success(this.$t('generic.delete.success'));
            }).
            catch(e => notification.error(this.$t(e.message)));
      }
    },
    deleteGroupDevices(groupId) {
      entityDataManager.
          getDevicesByGroupId(this.devices, groupId).
          map(device => device.id).
          forEach(deleteDeviceId => {
            this.$store.dispatch(actions.DELETE_DEVICE, {id: deleteDeviceId}).then(() => {
              const vehicleId = entityDataManager.getVehicleIdByDeviceId(this.vehicles, deleteDeviceId);

              if (vehicleId !== null) {
                this.$store.dispatch(actions.DELETE_VEHICLE, {id: vehicleId}).
                    then(() => {
                      this.$store.dispatch(actions.GET_VEHICLES).catch(e => notification.error(this.$t(e.message)));
                    }).
                    catch(e => notification.error(this.$t(e.message)));
              }

              this.$store.dispatch(actions.GET_DEVICES).catch(e => notification.error(this.$t(e.message)));
            });
          });
    },
  },
};
</script>
